import type { ThrowableId, VoiceTauntId } from '@big2/shared';
import { getAudioContext, isMutedGlobal } from './sound.js';

export const VOICE_TAUNT_SCRIPTS: Record<VoiceTauntId, { mn: string; en: string; icon: string }> = {
  hurry_sheep: { mn: 'Хурдлаач ээ, хонь минь!', en: 'Hurry up, sheep!', icon: '🐑' },
  wake_up: { mn: 'Сэрээрэй, маргааш боллоо!', en: 'Wake up, it is tomorrow!', icon: '⏰' },
  frozen: { mn: 'Хөлдчихсөн юм уу, яасан бэ?', en: 'Are you frozen or what?', icon: '🥶' },
  powder: { mn: 'Чамайг нунтаглана даа!', en: 'I will grind you to powder!', icon: '💥' },
  sorry_bro: { mn: 'Өө хөөрхий, уучлаарай найз минь!', en: 'Oh poor thing, sorry my friend!', icon: '🤝' },
  got_money: { mn: 'Мөнгө байна уу, хонгор минь?', en: 'Got any money, darling?', icon: '💰' },
};

function createNoiseBuffer(ctx: AudioContext, duration = 0.2): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * duration);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

/** 1. Hot Milk Tea Splash: Hissing white noise sweep + liquid bubble plop */
export function playTeaSplashSound() {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  // Hissing splash noise
  const noise = ctx.createBufferSource();
  noise.buffer = createNoiseBuffer(ctx, 0.35);
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(3200, now);
  filter.frequency.exponentialRampToValueAtTime(500, now + 0.3);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.01, now);
  gain.gain.linearRampToValueAtTime(0.28, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

  noise.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  noise.start(now);
  noise.stop(now + 0.35);

  // Liquid bubble drop
  [0.04, 0.12].forEach((offset, idx) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    const t0 = now + offset;
    osc.type = 'sine';
    osc.frequency.setValueAtTime(idx === 0 ? 550 : 420, t0);
    osc.frequency.exponentialRampToValueAtTime(180, t0 + 0.08);

    g.gain.setValueAtTime(0.2, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.08);

    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.09);
  });
}

/** 2. Slipper Slap: Sharp punchy smack + slap impact */
export function playSlipperSlapSound() {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  // Punchy low smack
  const osc = ctx.createOscillator();
  const oscGain = ctx.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(260, now);
  osc.frequency.exponentialRampToValueAtTime(45, now + 0.08);

  oscGain.gain.setValueAtTime(0.35, now);
  oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);

  osc.connect(oscGain);
  oscGain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.1);

  // Sharp slap noise
  const noise = ctx.createBufferSource();
  noise.buffer = createNoiseBuffer(ctx, 0.12);
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(1400, now);
  filter.Q.setValueAtTime(2.2, now);

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.01, now);
  noiseGain.gain.linearRampToValueAtTime(0.4, now + 0.005);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noise.start(now);
  noise.stop(now + 0.12);
}

/** 3. Raw Egg Splat: Squishy wet pop with downward chirp */
export function playEggSplatSound() {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(700, now);
  osc.frequency.exponentialRampToValueAtTime(95, now + 0.14);

  gain.gain.setValueAtTime(0.3, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.16);

  // Wet squish noise
  const noise = ctx.createBufferSource();
  noise.buffer = createNoiseBuffer(ctx, 0.18);
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1800, now);
  filter.frequency.exponentialRampToValueAtTime(300, now + 0.15);

  const nGain = ctx.createGain();
  nGain.gain.setValueAtTime(0.22, now);
  nGain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

  noise.connect(filter);
  filter.connect(nGain);
  nGain.connect(ctx.destination);
  noise.start(now);
  noise.stop(now + 0.19);
}

/** 4. Airag Toast: Bright celebratory wooden/silver bowl clink */
export function playAiragToastSound() {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  const harmonics = [1760, 2640, 3520];
  harmonics.forEach((freq, idx) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);

    const initialVol = 0.2 / (idx + 1);
    gain.gain.setValueAtTime(initialVol, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.55);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.6);
  });
}

/** 5. Cash Scatter: Fluttering banknotes and rustle */
export function playCashScatterSound() {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  for (let i = 0; i < 4; i++) {
    const t0 = now + i * 0.05;
    const noise = ctx.createBufferSource();
    noise.buffer = createNoiseBuffer(ctx, 0.06);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(2200 + i * 200, t0);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.18, t0);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.05);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start(t0);
    noise.stop(t0 + 0.06);
  }
}

/** 6. Shagai Click: Crisp bone-on-bone tap */
export function playShagaiClickSound() {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(1450, now);
  osc.frequency.exponentialRampToValueAtTime(220, now + 0.05);

  gain.gain.setValueAtTime(0.32, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.055);
}

/** Plays the sound effect for any given throwable item */
export function playThrowableSound(throwableId: ThrowableId) {
  switch (throwableId) {
    case 'tea':
      playTeaSplashSound();
      break;
    case 'slipper':
      playSlipperSlapSound();
      break;
    case 'egg':
      playEggSplatSound();
      break;
    case 'airag':
      playAiragToastSound();
      break;
    case 'cash':
      playCashScatterSound();
      break;
    case 'shagai':
      playShagaiClickSound();
      break;
  }
}

/** Plays a comedic audio fanfare/cue before speech */
function playTauntCue(tauntId: VoiceTauntId) {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';

  switch (tauntId) {
    case 'hurry_sheep':
      // Fast wobbling comedic bleat
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(320, now);
      osc.frequency.linearRampToValueAtTime(380, now + 0.08);
      osc.frequency.linearRampToValueAtTime(310, now + 0.16);
      gain.gain.setValueAtTime(0.12, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
      break;

    case 'wake_up':
      // 2-tone alarm beep
      osc.type = 'square';
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.setValueAtTime(1174, now + 0.08);
      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
      break;

    case 'frozen':
      // Cold descending slide
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1046, now);
      osc.frequency.exponentialRampToValueAtTime(260, now + 0.25);
      gain.gain.setValueAtTime(0.18, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
      break;

    case 'powder':
      // Dramatic low brass punch
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(140, now);
      osc.frequency.exponentialRampToValueAtTime(70, now + 0.2);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
      break;

    case 'sorry_bro':
      // Gentle slide
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(330, now + 0.22);
      gain.gain.setValueAtTime(0.14, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      break;

    case 'got_money':
      // High coin ping
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1568, now);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
      break;
  }

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.32);
}

/** Plays the voice taunt using browser speech synthesis and a comedic synthetic audio cue */
export function playVoiceTaunt(tauntId: VoiceTauntId, lang: 'mn' | 'en' = 'mn') {
  if (isMutedGlobal()) return;

  // Always play synthetic cue for instant comedic audio response
  playTauntCue(tauntId);

  const script = VOICE_TAUNT_SCRIPTS[tauntId];
  if (!script) return;

  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(script[lang]);
      utterance.lang = lang === 'mn' ? 'mn-MN' : 'en-US';
      utterance.rate = 1.05;
      utterance.pitch = 1.15;
      utterance.volume = 0.95;

      const voices = window.speechSynthesis.getVoices();
      const voice = voices.find((v) => v.lang.toLowerCase() === utterance.lang.toLowerCase())
        ?? voices.find((v) => v.lang.toLowerCase().startsWith(lang));
      if (voice) {
        utterance.voice = voice;
      }

      window.speechSynthesis.speak(utterance);
    } catch {
      // Audio cue already played as fallback
    }
  }
}
